// ═══════════════════════════════════════════════
// CashMyProperty — Public API Client (no auth)
// ═══════════════════════════════════════════════

const API_BASE = import.meta.env.VITE_PUBLIC_API_URL || '';

export interface PublicListing {
  id: number;
  tenant_id: number;
  uuid: string;
  title: string;
  slug: string;
  location: string;
  city: string;
  state_province: string;
  country: string;
  size_acres: number;
  expected_rent: number;
  rent_currency: string;
  rent_period: string;
  category: string;
  transaction_type: string;
  transaction_types: string[] | null;
  status: string;
  zoning: string | null;
  description: string | null;
  use_cases: string[] | null;
  features: string[] | null;
  images_json: string[] | null;
  owner_name: string | null;
  is_featured: number;
  is_verified: number;
  view_count: number;
  posted_date: string;
  created_at: string;
}

export interface PublicListingsResponse {
  success: boolean;
  message: string;
  data: PublicListing[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

export interface InquiryPayload {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  subject?: string;
  message: string;
  source?: string;
  listing_id?: number;
}

async function request<T>(endpoint: string, opts: { method?: string; body?: string; headers?: Record<string, string> } = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers || {}),
  };

  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: opts.method || 'GET',
    headers,
    ...(opts.body ? { body: opts.body } : {}),
  });

  const json = await res.json();

  if (!res.ok || !json.success) {
    throw new Error(json.error || json.message || `Request failed: ${res.status}`);
  }

  return json;
}

export const publicApi = {
  /** Get paginated public listings */
  getListings: (params: Record<string, string | number> = {}): Promise<PublicListingsResponse> => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    return request<PublicListingsResponse>(`/api/public/listings?${qs.toString()}`);
  },

  /** Get single public listing by ID */
  getListing: (id: number): Promise<{ success: boolean; message: string; data: PublicListing }> => {
    return request(`/api/public/listings/${id}`);
  },

  /** Submit a public inquiry */
  submitInquiry: (data: InquiryPayload): Promise<{ success: boolean; message: string; data: unknown }> => {
    return request('/api/public/inquiries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Submit a public feasibility report request */
  submitReport: (data: {
    location: string;
    land_size_acres: number;
    current_use?: string;
    road_access?: boolean;
    water_electricity?: boolean;
    latitude?: number;
    longitude?: number;
    requester_name?: string;
    requester_email: string;
    requester_phone?: string;
  }): Promise<{ success: boolean; message: string; data: unknown }> => {
    return request('/api/public/reports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Get public tenant settings (price visibility, features, etc.) */
  getSettings: (): Promise<{ success: boolean; message: string; data: { features: Record<string, unknown>; general: Record<string, unknown>; social: Record<string, unknown> } }> => {
    return request('/api/public/settings');
  },

  /** Submit property enquiry from detail page — creates a lead in the backend CRM */
  submitPropertyEnquiry: (data: {
    name: string;
    email?: string;
    phone: string;
    purpose?: string;
    description?: string;
    listing_id?: number;
    listing_title?: string;
    budget?: string;
  }): Promise<{ success: boolean; message: string; data: unknown }> => {
    return request('/api/public/enquiry', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Get all public CMS content — no auth required, returns pages with sections */
  getContent: (): Promise<{
    success: boolean;
    message: string;
    data: { pages: Array<{ page: string; sections: Array<{ section: string; data: string; updated_at: string }> }> };
  }> => {
    return request('/api/public/content');
  },
};